
import { LayoutDashboard, Settings, RefreshCw, LogOut, Lock, Check, X, ChevronDown, ChevronUp, Mail, Calendar, Clock, FileText, AlertCircle, Loader2, User, Phone, CheckCircle2, XCircle } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { apiRequest } from '../services/api';
import { Booking } from '../types';
import { LS_AUTH_KEY } from '../constants';
import { ConfigManager } from './ConfigManager';
import { StatusBadge } from './StatusBadge';
import Swal from 'sweetalert2';

interface AdminDashboardProps {
  onConfigChange: () => Promise<void>;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ onConfigChange }) => {
  const [auth, setAuth] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [tab, setTab] = useState<'bookings' | 'config'>('bookings');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);

  const performHardAuth = async (key: string) => {
    try {
      const res = await apiRequest<Booking[]>('get', 'GET', null, key);
      if (res.success === true && Array.isArray(res.data)) {
        setBookings(res.data.reverse());
        return { ok: true };
      }
      return { ok: false, msg: res.message || '密碼驗證失敗' };
    } catch (e) {
      return { ok: false, msg: '伺服器連線異常' };
    }
  };

  useEffect(() => {
    const init = async () => {
      const storedKey = localStorage.getItem(LS_AUTH_KEY);
      if (storedKey) {
        const res = await performHardAuth(storedKey);
        if (res.ok) setAuth(true);
        else localStorage.removeItem(LS_AUTH_KEY);
      }
      setIsVerifying(false);
    };
    init();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const input = password.trim();
    if (!input) return setError('請輸入管理密碼');

    setLoading(true);
    const res = await performHardAuth(input);
    
    if (res.ok) {
      localStorage.setItem(LS_AUTH_KEY, input);
      setAuth(true);
    } else {
      setError(res.msg);
      localStorage.removeItem(LS_AUTH_KEY);
    }
    setLoading(false);
  };

  const loadBookings = async () => {
    setLoading(true);
    const res = await apiRequest<Booking[]>('get');
    if (res.success && Array.isArray(res.data)) {
      setBookings(res.data.reverse());
    } else {
      handleLogout();
    }
    setLoading(false);
  };

  const updateStatus = async (id: string, status: string) => {
    const confirmText = status === 'CONFIRMED' ? '確認預約' : status === 'REJECTED' ? '拒絕預約' : '改為待審核';
    const result = await Swal.fire({
      title: `確定要${confirmText}嗎？`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: '確定',
      cancelButtonText: '取消',
      confirmButtonColor: status === 'CONFIRMED' ? '#2563eb' : status === 'REJECTED' ? '#ef4444' : '#64748b'
    });

    if (result.isConfirmed) {
      setLoading(true);
      const res = await apiRequest('update', 'POST', { id, status });
      if (res.success) {
        loadBookings();
        Swal.fire({ icon: 'success', title: '狀態已更新', timer: 1000, showConfirmButton: false });
      } else {
        Swal.fire({ icon: 'error', title: '更新失敗', text: res.message });
      }
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(LS_AUTH_KEY);
    setAuth(false);
    setPassword('');
    setBookings([]);
  };

  if (isVerifying) {
    return (
      <div className="w-full py-32 flex flex-col items-center justify-center">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-4" />
        <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Security Checking...</p>
      </div>
    );
  }

  if (!auth) {
    return (
      <div className="w-full max-w-md mx-auto mt-20 p-12 bg-white rounded-[3rem] shadow-2xl border border-slate-100 text-center animate-fade-in">
        <div className="w-20 h-20 bg-slate-900 text-white rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-2xl">
          <Lock className="w-10 h-10" />
        </div>
        <h2 className="text-2xl font-black mb-1 text-slate-800 tracking-tight">後台管理授權</h2>
        <p className="text-[10px] text-slate-400 mb-8 font-black uppercase tracking-[0.2em]">Restricted Access Area</p>
        
        <form onSubmit={handleLogin} className="space-y-6">
          <div className="relative">
            <input 
              type="password" 
              value={password} 
              onChange={e => setPassword(e.target.value)} 
              className={`w-full p-5 bg-slate-50 border-2 ${error ? 'border-rose-200 ring-4 ring-rose-50' : 'border-slate-100'} rounded-2xl outline-none focus:border-slate-900 transition-all font-mono text-center text-xl`} 
              placeholder="••••••••"
              required
            />
          </div>
          {error && (
            <div className="p-4 bg-rose-50 rounded-xl border border-rose-100 animate-shake">
              <p className="text-rose-600 text-[11px] font-black uppercase tracking-tight flex items-center justify-center gap-2">
                <AlertCircle className="w-4 h-4" /> {error}
              </p>
            </div>
          )}
          <button disabled={loading} className="w-full bg-slate-900 text-white py-5 rounded-2xl font-black hover:bg-black transition-all flex items-center justify-center gap-3 shadow-lg shadow-slate-200 disabled:opacity-50">
            {loading ? <RefreshCw className="w-6 h-6 animate-spin" /> : '驗證身分並登入'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto px-4 pb-20">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-12 gap-6">
        <div className="flex bg-slate-100 p-1.5 rounded-2xl shadow-inner">
          <button onClick={() => setTab('bookings')} className={`px-8 py-3 rounded-xl font-black transition-all flex items-center gap-2 ${tab === 'bookings' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-400'}`}>
            <Calendar className="w-4 h-4" /> 預約管理
          </button>
          <button onClick={() => setTab('config')} className={`px-8 py-3 rounded-xl font-black transition-all flex items-center gap-2 ${tab === 'config' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-400'}`}>
            <Settings className="w-4 h-4" /> 系統設定
          </button>
        </div>
        <div className="flex gap-3">
          <button onClick={loadBookings} title="重新整理" className="p-4 bg-white border border-slate-200 rounded-2xl text-slate-400 hover:bg-slate-50 shadow-sm transition-all active:rotate-180">
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={handleLogout} className="px-6 py-4 text-rose-600 font-bold bg-white border border-rose-100 rounded-2xl hover:bg-rose-50 shadow-sm transition-all flex items-center gap-2">
            <LogOut className="w-5 h-5" /> 登出系統
          </button>
        </div>
      </div>

      {tab === 'config' ? <ConfigManager onConfigChange={onConfigChange} /> : (
        <div className="grid grid-cols-1 gap-6">
          {bookings.length === 0 ? (
            <div className="py-20 text-center bg-white rounded-[3rem] border border-dashed border-slate-200">
              <Calendar className="w-12 h-12 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-400 font-bold">目前尚無預約資料</p>
            </div>
          ) : (
            bookings.map(b => (
              <div key={b.id} className="bg-white rounded-[2.5rem] border border-slate-100 shadow-xl shadow-slate-200/40 overflow-hidden flex flex-col md:flex-row group transition-all hover:border-blue-200">
                <div className={`w-2 md:w-3 ${b.status === 'CONFIRMED' ? 'bg-emerald-500' : b.status === 'REJECTED' ? 'bg-rose-500' : 'bg-amber-400'}`}></div>
                <div className="flex-1 p-8">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div className="flex items-center gap-5">
                      <div className="w-16 h-16 rounded-3xl bg-slate-50 flex items-center justify-center font-black text-slate-300 text-2xl border border-slate-100">
                        {b.name.charAt(0)}
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-xl font-black text-slate-800">{b.name}</h3>
                          <StatusBadge status={b.status} />
                        </div>
                        <div className="flex items-center gap-4 text-[11px] font-black uppercase tracking-widest text-slate-400">
                          <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {b.date}</span>
                          <span className="flex items-center gap-1.5 text-blue-500/60"><Clock className="w-3.5 h-3.5" /> {b.time}</span>
                        </div>
                      </div>
                    </div>

                    <div className="w-full md:w-auto flex flex-col sm:flex-row gap-3">
                      {b.status === 'PENDING' ? (
                        <>
                          <button onClick={() => updateStatus(b.id, 'CONFIRMED')} className="flex-1 px-8 py-4 bg-blue-600 text-white rounded-2xl font-black hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-100">
                            <CheckCircle2 className="w-5 h-5" /> 確認預約
                          </button>
                          <button onClick={() => updateStatus(b.id, 'REJECTED')} className="flex-1 px-8 py-4 bg-white border-2 border-rose-100 text-rose-500 rounded-2xl font-black hover:bg-rose-500 hover:text-white transition-all flex items-center justify-center gap-2">
                            <XCircle className="w-5 h-5" /> 拒絕預約
                          </button>
                        </>
                      ) : (
                        <button onClick={() => updateStatus(b.id, 'PENDING')} className="px-6 py-4 bg-slate-50 text-slate-400 border border-slate-200 rounded-2xl font-bold hover:bg-white hover:text-slate-600 transition-all text-sm">
                          回覆為待審核
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-8 pt-8 border-t border-slate-50 grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="p-2 bg-slate-50 rounded-lg text-slate-400"><Phone className="w-4 h-4" /></div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">聯絡資訊</p>
                          <p className="text-sm font-bold text-slate-700">{b.email}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="p-2 bg-slate-50 rounded-lg text-slate-400"><LayoutDashboard className="w-4 h-4" /></div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">預約主題</p>
                          <p className="text-sm font-bold text-slate-700">{b.topic}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-slate-50/50 p-6 rounded-3xl border border-slate-100">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5" /> 需求備註說明
                      </p>
                      <p className="text-sm text-slate-600 leading-relaxed italic font-medium">
                        {b.description || '申請人未提供額外備註。'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
